import React, { useCallback, useEffect, useMemo, useState } from "react";

import { format } from "date-fns";
import { RouteSuspenseFallback } from "routes/route-suspense-fallback.component";

import { useAppSelector } from "app/hooks";
import { CustomFilterOption } from "components/custom-filters-list/custom-filters-list.component";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegionClinicsIds,
  selectCurrentUser,
  selectUserClinicsIds
} from "features/auth/auth-slice";
import useFaxAutoRetryEnabled from "features/faxes/hooks/use-fax-auto-retry.hook";
import useFaxFailureDetailEnabled from "features/faxes/hooks/use-fax-failure-detail.hook";
import useFaxUnassignedQueueEnabled, {
  useFaxUnassignedQueueResolved
} from "features/faxes/hooks/use-fax-unassigned-queue.hook";
import usePaginationV2 from "hooks/use-pagination-v2.hook";
import usePersistedState from "hooks/use-persisted-state";
import { getFaxesQuery } from "services/faxes";
import {
  FAXES_DIRECTION_INBOUND,
  FaxFilters,
  FaxResponse,
  FaxesQueryParams
} from "services/types/fax.type";
import { determineClinicFiltering } from "utils/clinics.util";

import useFaxTabDateRanges, {
  FaxDateScope,
  FaxTabDateRange,
  getFaxDateScope
} from "./use-fax-tab-date-ranges.hook";
import useFaxesCustomFilterOptions, {
  FaxesCustomFiltersValues
} from "./use-faxes-custom-filter-options.hook";

type DateRange = {
  after_date: string | null;
  before_date: string | null;
};

// The list API takes plain dates; the picker hands over ISO strings of local midnights.
const formatDate = (dateStr: string | null) => {
  try {
    return dateStr ? format(new Date(dateStr), "yyyy-MM-dd") : undefined;
  } catch (e) {
    console.error(`Error parsing date: ${dateStr}, ${e}`);
    return undefined;
  }
};

const FaxesListContext = React.createContext<
  | ({
      faxes: FaxResponse[];
      fetchMore: () => void;
      isLoading: boolean;
      currentFilters: FaxesQueryParams;
      setCurrentFilters: React.Dispatch<React.SetStateAction<FaxesQueryParams>>;
      updateCustomFilter: (field: keyof FaxesQueryParams, value: any) => void;
      customFilters: CustomFilterOption[];
      selectedCustomFilters: FaxesCustomFiltersValues;
      refetch: () => void;
      /** Sets the date range of the tab being shown (ENG-2511: each tab keeps its own). */
      updateDateRange: (range: FaxTabDateRange) => void;
      dateScope: FaxDateScope;
      datePreset: FaxTabDateRange["preset"];
      includeOtherClinics: boolean;
      setIncludeOtherClinics: (value: boolean) => void;
      updatePaginatedData: (id: number) => void;
      isDataPending: boolean;
      isUnassignedQueueEnabled: boolean;
      hasCurrentClinic: boolean;
    } & DateRange)
  | null
>(null);

type FaxesProviderProps = {
  children?: React.ReactNode;
};

/**
 * Waits for LaunchDarkly's answer for this user before the Fax Center mounts (ENG-2511). The queue
 * flag picks the default tab and whether opening the page marks every fax read, and before
 * identify() it reads off, so rendering early would run that read-all and wipe the queue.
 */
export function FaxesListProvider({ children }: FaxesProviderProps) {
  const isQueueFlagResolved = useFaxUnassignedQueueResolved();
  if (!isQueueFlagResolved) return <RouteSuspenseFallback />;

  return <FaxesListStateProvider>{children}</FaxesListStateProvider>;
}

function FaxesListStateProvider({ children }: FaxesProviderProps) {
  const [includeOtherClinics, setIncludeOtherClinics] =
    usePersistedState<boolean>("useFaxes/includeOtherClinics", false);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const isFailureDetailEnabled = useFaxFailureDetailEnabled();
  const isAutoRetryEnabled = useFaxAutoRetryEnabled();
  const isUnassignedQueueEnabled = useFaxUnassignedQueueEnabled();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentRegionClinicsIds = useAppSelector(selectCurrentRegionClinicsIds);
  const userClinicsIds = useAppSelector(selectUserClinicsIds);
  const currentUser = useAppSelector(selectCurrentUser);

  const { ranges: dateRanges, updateRange: updateTabDateRange } =
    useFaxTabDateRanges(isUnassignedQueueEnabled);
  // All Faxes and Blocked filter through currentFilters, as they always have.
  const { after_date, before_date } = dateRanges.standard;

  const [selectedCustomFilters, setSelectedCustomFilters] =
    useState<FaxesCustomFiltersValues>({});

  const faxesFilters: FaxesQueryParams = useMemo(() => {
    const clinic = !currentPractice?.limit_to_current_clinic
      ? determineClinicFiltering({
          currentClinic,
          includeOtherClinics,
          currentRegionClinicsIds,
          isShowOwnClinicsOnlyEnabled: currentUser?.show_own_clinics_only,
          userClinicsIds
        })
      : currentClinic?.id;

    return {
      query: getFaxesQuery(
        isFailureDetailEnabled,
        isAutoRetryEnabled,
        isUnassignedQueueEnabled
      ),
      clinic
    };
  }, [
    currentClinic,
    currentPractice?.limit_to_current_clinic,
    currentRegionClinicsIds,
    userClinicsIds,
    currentUser?.show_own_clinics_only,
    includeOtherClinics,
    isFailureDetailEnabled,
    isAutoRetryEnabled,
    isUnassignedQueueEnabled
  ]);

  // With the queue on, the page opens on Unassigned Faxes: inbound, no patient (ENG-2511).
  const [currentFilters, setCurrentFilters] = useState<FaxesQueryParams>(() =>
    isUnassignedQueueEnabled
      ? {
          ...faxesFilters,
          unassigned: true
        }
      : faxesFilters
  );

  const updateCustomFilter = useCallback(
    (field: keyof FaxesQueryParams, value: any) => {
      return setCurrentFilters((prevFilters) => ({
        ...prevFilters,
        [field]: value
      }));
    },
    [setCurrentFilters]
  );

  const customFilters = useFaxesCustomFilterOptions({
    updateCustomFilter,
    selectedCustomFilters,
    setSelectedCustomFilters
  });

  useEffect(() => {
    setCurrentFilters((prev) => ({
      ...prev,
      after_date: formatDate(after_date),
      before_date: formatDate(before_date)
    }));
  }, [after_date, before_date]);

  useEffect(() => {
    setCurrentFilters((prevFilters) => ({
      ...prevFilters,
      ...faxesFilters
    }));
  }, [faxesFilters]);

  // What the list actually asks for. The Unassigned queue is inbound faxes with no patient, so it
  // overrides the Direction and Patient filters (locked on that tab) instead of overwriting them:
  // they come back on All Faxes. The queue and Archived read their own date range, which starts
  // empty: the page's Today default would hide older faxes nobody has handled, or a fax archived
  // today but received last week. Archived lists the most recently archived first. Flag off,
  // `unassigned` is never sent. Memoised: a new params object on every render would restart the
  // paginated fetch.
  const queueRange = dateRanges.queue;
  const archivedRange = dateRanges.archived;
  const listParams = useMemo((): FaxFilters => {
    if (!isUnassignedQueueEnabled) {
      return currentFilters.unassigned
        ? { ...currentFilters, unassigned: undefined }
        : currentFilters;
    }
    if (currentFilters.unassigned) {
      return {
        ...currentFilters,
        direction: FAXES_DIRECTION_INBOUND,
        patient: undefined,
        after_date: formatDate(queueRange.after_date),
        before_date: formatDate(queueRange.before_date)
      };
    }
    if (currentFilters.deactivated) {
      return {
        ...currentFilters,
        after_date: formatDate(archivedRange.after_date),
        before_date: formatDate(archivedRange.before_date),
        ordering: "-deactivated_at,-id"
      };
    }
    return currentFilters;
  }, [currentFilters, isUnassignedQueueEnabled, queueRange, archivedRange]);

  const dateScope = getFaxDateScope(currentFilters, isUnassignedQueueEnabled);
  const shownDateRange = dateRanges[dateScope];
  const updateDateRange = useCallback(
    (range: FaxTabDateRange) => updateTabDateRange(dateScope, range),
    [dateScope, updateTabDateRange]
  );

  const {
    data: faxes = [],
    isLoading,
    fetchMore,
    refetch,
    updatePaginatedData,
    isDataPending
  } = usePaginationV2<FaxFilters, FaxResponse>({
    endpointName: "getFaxesPaginated",
    limit: 25,
    params: listParams
  });

  return (
    <FaxesListContext.Provider
      value={{
        faxes,
        fetchMore,
        isLoading,
        currentFilters,
        setCurrentFilters,
        customFilters,
        updateCustomFilter,
        selectedCustomFilters,
        refetch,
        after_date: shownDateRange.after_date,
        before_date: shownDateRange.before_date,
        updateDateRange,
        dateScope,
        datePreset: shownDateRange.preset,
        includeOtherClinics,
        setIncludeOtherClinics,
        updatePaginatedData,
        isDataPending,
        isUnassignedQueueEnabled,
        hasCurrentClinic: Boolean(currentClinic?.id)
      }}
    >
      {children}
    </FaxesListContext.Provider>
  );
}

export default function useFaxesList() {
  const context = React.useContext(FaxesListContext);
  if (!context) {
    throw new Error(`useFaxesList must be used within a FaxesListProvider`);
  }

  return context;
}
